import { useAppStore } from "@/stores";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  parse,
  format,
  subDays,
} from "date-fns";
import { useMemo, useState } from "react";
import { Database, TODO } from "@revelate/types";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip as RechartsTooltip,
  TooltipProps as RechartsTooltipProps,
  Legend,
  ScatterChart,
  Scatter,
  ComposedChart,
  Area,
  CartesianGrid,
  Line,
  Cell,
  FunnelChart,
  Funnel,
  LabelList,
  LineChart,
  ReferenceArea,
  Sankey,
  TooltipProps,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import {
  formatRiskPointChange,
  formatRiskPoints,
  mapRiskName,
  type RiskIndicator,
} from "@/revenue-intelligence/lib/risk-contracts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { InfoIcon } from "lucide-react";
import {
  formatCurrencyValue,
  type ManagementReviewPipelineMovementRow,
  type ManagementReviewWinLossFlowRow,
} from "../lib/risk";

type DataBreakdown =
  | "by_pipeline"
  // | "by_product"
  | "by_month"
  | "by_sales_rep"
  // | "by_segment"
  ;


// Risk badge
const riskBadgeVariants = {
  high: "rejected",
  medium: "approved",
  low: "paid",
};

const getRiskBucket = (score: number) => {
  if (score > 50) return "high"
  if (score > 25) return "medium"
  return "low"
}

interface PipelineRiskBubbleChartProps {
  data: any[],
  companyCurrency: Database["public"]["Tables"]["currencies"]["Row"] | null
}

// FIXME: Deals showing company currency, they should be showing their own currency
export function PipelineRiskBubbleChart({ data, companyCurrency: _ }: PipelineRiskBubbleChartProps) {
  return (
    <div className="h-[304px]">
      <ResponsiveContainer width="100%">
        <ScatterChart>
          <CartesianGrid
            strokeDasharray="3 3"
          />
          <XAxis
            type="number"
            dataKey="stage_probability"
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tickFormatter={(value) => {
              switch (value) {
                case 0:
                  return "Discovery";
                case 50:
                  return "Proposal";
                case 75:
                  return "";
                case 100:
                  return "Commit";
                default:
                  return "";
              }
            }}
            tick={{ fontSize: "12px" }}
            tickLine={false}
          />

          <YAxis
            type="number"
            dataKey="risk_score"
            domain={[
              (dataMin: number) => Math.max(dataMin * 0.95, 0),
              (dataMax: number) => Math.min(dataMax * 1.05, 100),
            ]}
            ticks={[0, 25, 50, 100]}
            tickFormatter={(value) => {
              switch (value) {
                case 25:
                  return "Low";
                case 50:
                  return "Medium";
                case 100:
                  return "High";
                default:
                  return "";
              }
            }}
            fontSize={12}
            width={50}
          />

          <ZAxis
            type="number"
            dataKey="value"
            range={[100, 1000]}
            name="Deal Value"
          />

          <RechartsTooltip
            cursor={{ strokeDasharray: "3 3" }}
            content={<CustomTooltipPipelineBubble />}
          />

          {/* <Scatter */}
          {/*   data={data} */}
          {/*   fill="#3A3361" */}
          {/*   stroke="#FFF" */}
          {/*   strokeWidth="1px" */}
          {/* /> */}

          <Scatter data={data} stroke="#FFF" strokeWidth="1px">
            {data.map((entry, index) => {
              let color;

              if (entry.risk_score >= 50) {
                color = "#7C2E2E";
              } else if (entry.risk_score >= 25) {
                color = "#5E548E";
              } else {
                color = "#0D6E55";
              }

              return <Cell key={`cell-${index}`} fill={color} />;
            })}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}

function CustomTooltipPipelineBubble({
  active,
  payload,
}: RechartsTooltipProps<number, string>) {
  const { companyCurrency } = useAppStore((state) => state);
  const currencyCode = companyCurrency?.code ?? "SEK";

  if (!active || !payload || payload.length === 0) return null;

  const deal = payload[0].payload;

  const currencyFormatter = new Intl.NumberFormat("sv-SE");

  const formatPercent = (value: number) => `${Math.round(value)}%`;

  return (
    <Card className="w-64 p-0">
      <CardHeader className="p-4">
        {/* Title */}
        <CardTitle className="text-xs font-bold font-display pb-2">
          {deal?.name}
        </CardTitle>

        {/* Content */}
        <div className="space-y-1 text-xs">
          <div className="flex items-center justify-between gap-4">
            <span>Deal value</span>
            <span className="flex items-center gap-2">
              <span>{currencyFormatter.format(Number(deal?.value))} {currencyCode}</span>
            </span>
          </div>

          <div className="flex items-center justify-between gap-4">
            <span>Owner</span>
            <span className="flex items-center gap-2">
              <span>{deal?.owner_name}</span>
            </span>
          </div>

          <div className="flex items-center justify-between gap-4">
            <span>Close probability</span>
            <span>{deal.stage_probability ? formatPercent(Number(deal.stage_probability)) : "Unknown"}</span>
          </div>

          {!!deal?.risk_score && (
            <div className="flex items-center justify-between gap-4 pb-1">
              <span>Risk</span>
              <Badge
                variant={riskBadgeVariants[getRiskBucket(deal.risk_score)] as any}
                className="capitalize text-[10px] px-1.5 py-0"
              >
                {getRiskBucket(deal.risk_score)}
              </Badge>
            </div>
          )}
        </div>
      </CardHeader>
    </Card>
  );
}


interface ForecastChartProps {
  data: TODO[],
  companyCurrency: Database["public"]["Tables"]["currencies"]["Row"] | null
}

export function ForecastChart({ data, companyCurrency }: ForecastChartProps) {
  const formatAxisCurrency = (value: number) =>
    formatAxisTickValue(value, companyCurrency);

  return (
    <div className="h-72">
      <ResponsiveContainer width="100%">
        <ComposedChart data={data}>
          <XAxis
            dataKey="month"
            tickFormatter={(value) => {
              const date = parse(value, "yyyy-MM", new Date());
              return format(date, "MMM");
            }}
            fontSize={12}
          />

          <YAxis
            fontSize={12}
            tickFormatter={(value) => formatAxisCurrency(value)}
            domain={[
              0,
              (dataMax: number) => dataMax * 1.05,
            ]}
            width={80}
          />

          <RechartsTooltip
            formatter={(value: number) => value.toLocaleString()}
            content={<CustomTooltipForecastChart />}
          />

          <Legend content={<CustomLegendForecastChart />} />

          <Area
            type="linear"
            dataKey="target"
            stroke="#3A3361"
            fill="#3A3361"
            name="Target"
            strokeWidth={2}
            fillOpacity={0.15}
          />

          <Area
            type="linear"
            dataKey="total_minus_risk"
            stroke="#9F86C0"
            fill="white"
            name="Forecast"
            strokeWidth={2}
            fillOpacity={1}
          />

          <Line
            type="linear"
            dataKey="target"
            stroke="#3A3361"
            strokeWidth={2}
          />

          <Line
            type="linear"
            dataKey="crm_forecast"
            // stroke="#9CA3AF" // tailwind bg-gray-400
            // stroke="#1F2937" // tailwind bg-gray-800
            // stroke="#374151" // tailwind bg-gray-700
            stroke="#6B7280" // tailwind bg-gray-600
            name="CRM Forecast"
            strokeWidth={2}
            strokeDasharray="4 4"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div >
  );
}

function CustomLegendForecastChart() {
  return (
    <div className="flex flex-row justify-center items-center gap-x-5 mt-1">
      <div className="flex flex-row justify-center items-center gap-x-1.5">
        <div className="w-4 h-1 rounded-full bg-[#3A3361]" />
        <p className="text-gray-500 text-xs">
          Target
        </p>
      </div>
      <div className="flex flex-row justify-center items-center gap-x-1.5">
        <div className="w-4 h-1 rounded-full bg-[#9F86C0]" />
        <p className="text-gray-500 text-xs font-normal">
          Risk adjusted forecast
        </p>
      </div>
      <div className="flex flex-row justify-center items-center gap-x-1.5">
        <div className="w-4 h-1 rounded-full bg-[#9F86C0] opacity-50" />
        <p className="text-gray-500 text-xs font-normal">
          Revenue gap
        </p>
      </div>
      <div className="flex flex-row justify-center items-center gap-x-1.5">
        <div className="w-4 h-1 rounded-full bg-gray-400" />
        <p className="text-gray-500 text-xs font-normal">
          CRM forecast
        </p>
      </div>
    </div>
  );
};

function CustomTooltipForecastChart({ active, payload, label }: RechartsTooltipProps<number, string>) {
  const { companyCurrency } = useAppStore((state) => state);
  const currencyCode = companyCurrency?.code ?? "SEK";

  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  const month = row?.month;

  const target = payload.find(p => p.dataKey === "target")?.value ?? 0;
  const forecast = payload.find(p => p.dataKey === "total_minus_risk")?.value ?? 0;
  const crmForecast = payload.find(p => p.dataKey === "crm_forecast")?.value ?? 0;
  const revenueGap = Number(forecast) - Number(target);

  const currencyFormatter = new Intl.NumberFormat("sv-SE")

  return (
    <Card className="w-72 p-0" key={label}>
      <CardHeader className="p-4">
        <CardTitle className="text-xs font-bold font-display pb-2">
          {month}
        </CardTitle>

        <div className="space-y-1 text-xs">
          <div className="flex items-center justify-between gap-4">
            <span>Target</span>
            <span className="flex items-center gap-2 shrink-0">
              <span className="h-2 w-2 rounded-full bg-[#3A3361]" />
              <span>{currencyFormatter.format(Number(target))} {currencyCode}</span>
            </span>
          </div>

          <div className="flex items-center justify-between gap-4">
            <span>CRM Forecast</span>
            <span className="flex items-center gap-2 shrink-0">
              <span className="h-2 w-2 rounded-full bg-gray-400" />
              <span>{currencyFormatter.format(Number(crmForecast))} {currencyCode}</span>
            </span>
          </div>

          <div className="flex items-center justify-between gap-4 pb-1">
            <span>Risk-adjusted Forecast</span>
            <span className="flex items-center gap-2 shrink-0">
              <span className="h-2 w-2 rounded-full bg-[#9F86C0]" />
              <span>{currencyFormatter.format(Number(forecast))} {currencyCode}</span>
            </span>
          </div>

          <div className="border-t border-gray-200 pt-2 flex items-center justify-between gap-4">
            <span>Revenue gap</span>
            <span className={cn("shrink-0", revenueGap < 0 ? "text-red-600" : "text-green-700")}>
              {revenueGap > 0 && "+"}{currencyFormatter.format(revenueGap)} {currencyCode}
            </span>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
};

interface ForecastWaterfallChartProps {
  data: { type: string; label: string; value: number }[];
  companyCurrency?: any;
}

export function ForecastWaterfallChart({
  data,
  companyCurrency,
}: ForecastWaterfallChartProps) {
  const formatAxisCurrency = (value: number) =>
    formatAxisTickValue(value, companyCurrency);

  return (
    <div className="h-72 bg-white p-4">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <XAxis
            dataKey="label"
            fontSize={12}
          />
          <YAxis
            fontSize={12}
            tickFormatter={formatAxisCurrency}
            width={80}
          />
          <RechartsTooltip
            content={<CustomTooltipForecastWaterfallChart />}
          />
          {/* Invisible base to "push" bars to the correct starting point */}
          <Bar
            dataKey="cumulativeBefore"
            stackId="stack"
            fill="transparent"
          />

          {/* Bar */}
          <Bar
            dataKey="value"
            stackId="stack"
            fill="#3A3361"
            radius={[2, 2, 2, 2]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function CustomTooltipForecastWaterfallChart({
  active,
  payload,
  label,
}: RechartsTooltipProps<number, string>) {
  const { companyCurrency } = useAppStore((state) => state);
  const currencyCode = companyCurrency?.code ?? "SEK";

  if (!active || !payload || payload.length === 0) return null;

  const row = payload[0].payload;
  const currencyFormatter = new Intl.NumberFormat("sv-SE");
  const value = Number(row.value ?? 0);

  const isRisk = row.type === "risk";
  const displayValue = isRisk ? value * -1 : value;
  const isIncreaseInRisk = isRisk && value < 0; // Negative values used for chart when risk is positive
  const isDecreaseInRisk = isRisk && value > 0; // Negative values used for chart when risk is positive

  const description = !isRisk
    ? "Forecast"
    : isIncreaseInRisk
      ? "Increase in risk"
      : isDecreaseInRisk
        ? "Decrease in risk"
        : "Change in risk";

  return (
    <Card className="w-72 p-0" key={label}>
      <CardHeader className="p-4">
        <CardTitle className="text-xs font-bold font-display pb-2">
          {row.label}
        </CardTitle>

        <div className="space-y-1 text-xs">
          <div className="flex items-center justify-between gap-4">
            <span>
              {description}
            </span>

            <span className="flex items-center gap-2 shrink-0">
              <span>
                {currencyFormatter.format(Math.abs(displayValue))} {currencyCode}
              </span>
            </span>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
}

interface RevenueVsPlanChartProps {
  data: TODO[],
  companyCurrency: Database["public"]["Tables"]["currencies"]["Row"] | null
}

export function RevenueVsPlanChart({ data, companyCurrency }: RevenueVsPlanChartProps) {

  // Get the largest value seen in all months
  // and scale all bars to that maximum
  // Default to 1 in case all 0 values found, to avoid division by 0
  const maxValueFromAllTargetsAndForecasts = data
    .map(row => Math.max(row.target, row.total_minus_risk))
    .reduce((acc, val) => Math.max(acc, val), 1);

  return (
    <div className="flex flex-col gap-y-6">
      {/* Months */}
      <div className="flex flex-col gap-y-8 pt-2">
        {data.map(row => {
          const monthLabel = format(`${row.month}-01`, "MMM");

          const targetBarValue = row.target * 100 / maxValueFromAllTargetsAndForecasts;
          const wonBarValue = row.won * 100 / maxValueFromAllTargetsAndForecasts;
          const forecastedToWinBarValue = row.total_minus_risk * 100 / maxValueFromAllTargetsAndForecasts;

          // TODO: Set bar colors
          // If a month's won+forecastedToWin >= target
          // Both bars should be green shades
          // Otherwise both bars should be red shades
          // For now, just using green in every case

          return (
            <div key={row.month}>
              <div className="flex flex-row justify-between items-center">
                {/* Month and bars */}
                <div className="flex-1 flex flex-row gap-x-4 items-center">
                  <p className="text-sm text-gray-500 w-6">
                    {monthLabel}
                  </p>
                  <div className="w-full flex flex-col gap-y-2">
                    <div className="h-2 relative z-10">
                      {/* Below: Forecasted to win */}
                      <Progress
                        value={forecastedToWinBarValue}
                        className="h-2 rounded-[3px] absolute inset-0 z-10"
                        indicatorClassName="bg-green-500/50"
                      />
                      {/* Superimposed above: won */}
                      <Progress
                        value={wonBarValue}
                        className="h-2 rounded-[3px] absolute inset-0 z-20 bg-transparent"
                        // indicatorClassName={cn(row.total_minus_risk < row.target ? "bg-red-600" : "bg-green-700")}
                        indicatorClassName="bg-green-700"
                      />
                    </div>
                    <Progress
                      value={targetBarValue}
                      className="h-2 rounded-[3px]"
                    />
                  </div>
                </div>
                {/* Amounts */}
                <div className="w-24 h-full flex flex-col items-end">
                  <p className="text-xs">
                    {formatCurrencyValue(row.total_minus_risk, companyCurrency, 1)}
                  </p>
                  <p className="text-[10px] text-gray-500">
                    target {formatCurrencyValue(row.target, companyCurrency, 1)}
                  </p>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {/* Legend */}
      <div>
        <div className="flex flex-row gap-x-5">
          <div className="flex flex-row items-center gap-x-1.5">
            <div className="h-2 w-5 bg-green-700 rounded-full" />
            <p className="text-xs text-gray-500">
              Won
            </p>
          </div>
          <div className="flex flex-row items-center gap-x-1.5">
            <div className="h-2 w-5 bg-green-500/50 rounded-full" />
            <p className="text-xs text-gray-500">
              Forecasted
            </p>
          </div>
          <div className="flex flex-row items-center gap-x-1.5">
            <div className="h-2 w-5 bg-[#3A3361] rounded-full" />
            <p className="text-xs text-gray-500">
              Target
            </p>
          </div>
        </div>
      </div>
    </div >
  );
}

interface ForecastDriverChartProps {
  data: { type: string; label: string; value: number }[];
  companyCurrency?: any;
  currentValueLabel?: string;
}

export function ForecastDriverChart({
  data,
  companyCurrency,
  currentValueLabel = "Current forecast",
}: ForecastDriverChartProps) {
  const forecast30dAgo = data.find(row => row.type === "start")?.value ?? 0;
  const forecastEndOfPeriod = data.find(row => row.type === "end")?.value ?? 0;

  // FIXME: Temporary hotfix to just call the difference "net new deals"
  // The real calculation for net new deals must come from the 
  // same math that produces the remaining chart data
  // and whether that matches or not will determine "true" correctness
  const netRisk = data
    .filter(row => row.type === "risk")
    .map(row => row.value)
    .reduce((acc, val) => acc + val, 0);
  const forecastDiff = forecastEndOfPeriod - forecast30dAgo;
  const netNewDeals = forecastDiff - netRisk;

  // Get the largest value seen in all months
  // and scale all bars to that maximum
  // Default to 1 in case all 0 values found, to avoid division by 0
  const maxValueSeen = data
    .map(row => Math.abs(row.value))
    .reduce((acc, val) => Math.max(acc, val), Math.max(1, netNewDeals));

  return (
    <div className="h-52">
      <div className="flex flex-col gap-y-4">

        <div className="flex flex-col gap-y-2">
          <Separator />
          <div className="flex flex-row justify-between items-center">
            <div className="flex flex-row gap-x-0 flex-1 items-center">
              <p className="text-xs w-48">
                30 days ago
              </p>
              <Progress
                value={forecast30dAgo * 100 / maxValueSeen}
                className="h-2 rounded-[3px]"
              />
            </div>
            <p className="text-right text-xs w-24">
              {formatCurrencyValue(forecast30dAgo, companyCurrency, 1)}
            </p>
          </div>
          <Separator />
        </div>

        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-row gap-x-0 flex-1 items-center">
            <p className="text-xs w-48">
              Net new deals
            </p>
            <Progress
              value={netNewDeals * 100 / maxValueSeen}
              className="h-2 rounded-[3px]"
              indicatorClassName="bg-green-700"
            />
          </div>
          <p className="text-right text-xs w-24">
            {formatCurrencyValue(netNewDeals, companyCurrency, 1)}
          </p>
        </div>

        {data.filter(row => row.type === "risk").map(row => (
          <div key={row.label} className="flex flex-row justify-between items-center">
            <div className="flex flex-row gap-x-0 flex-1 items-center">
              <p className="text-xs w-48">
                {row.label}
              </p>
              <Progress
                value={Math.abs(row.value) * 100 / maxValueSeen}
                className="h-2 rounded-[3px]"
                indicatorClassName="bg-red-600"
              />
            </div>
            <p className="text-right text-xs w-24">
              {formatCurrencyValue(row.value, companyCurrency, 1)}
            </p>
          </div>
        ))}

        <div className="flex flex-col gap-y-2">
          <Separator />
          <div className="flex flex-row justify-between items-center">
            <div className="flex flex-row gap-x-0 flex-1 items-center">
              <p className="text-xs w-48">
                {currentValueLabel}
              </p>
              <Progress
                value={forecastEndOfPeriod * 100 / maxValueSeen}
                className="h-2 rounded-[3px]"
              />
            </div>
            <p className="text-right text-xs w-24">
              {formatCurrencyValue(forecastEndOfPeriod, companyCurrency, 1)}
            </p>
          </div>
          <Separator />
        </div>
      </div>
    </div>
  );
}

interface PipelineCreatedChartProps {
  data: Record<string, {
    categoryTotal: number;
    totalOfAllCategories: number;
    pct: number
  }>;
  pipelineCreatedBreakdown: "source" | "segment";
  companyCurrency?: any;
  emptyMessage?: string;
}

function isCatchAllCategory(name: string) {
  return ["not specified", "unclassified", "unclassified loss", "other"].includes(name.toLowerCase());
}

export function PipelineCreatedChart({
  data,
  pipelineCreatedBreakdown: _,
  companyCurrency,
  emptyMessage = "Data not available.",
}: PipelineCreatedChartProps) {

  const entries = Object.entries(data)
    .filter(([, item]) => item.categoryTotal > 0)
    .toSorted((a, b) => {
      if (isCatchAllCategory(a[0]) !== isCatchAllCategory(b[0])) return isCatchAllCategory(a[0]) ? 1 : -1;
      return b[1].categoryTotal - a[1].categoryTotal;
    });

  if (!entries.length) {
    return (
      <div className="h-52">
        <p className="text-sm text-gray-500">
          {emptyMessage}
        </p>
      </div>
    )
  }

  return (
    <div className="h-52 overflow-y-auto pr-1">
      <div className="flex flex-col gap-y-4 pt-4">
        {entries
          .slice(0, 6)
          .map(([category, attainment]) => (
            <div key={category} className="flex flex-row justify-between items-center">
              <div className="flex flex-row gap-x-0 flex-1 items-center">
                <p className="text-xs w-60">
                  {category}
                </p>
                <Progress
                  value={attainment.pct}
                  className="h-2 rounded-[3px]"
                />
              </div>
              <div className="flex flex-row gap-x-1 justify-end items-center w-32">
                <p className="text-right text-xs w-24">
                  {formatCurrencyValue(attainment.categoryTotal, companyCurrency, 1)}
                </p>
                <p className="text-center text-[8px] text-gray-400 w-2">
                  •
                </p>
                <p className="text-right text-xs w-8 text-gray-500">
                  {attainment.pct.toFixed(0)}%
                </p>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

interface WonByCategoryChartProps {
  data: Record<string, {
    categoryTotal: number;
    categoryWonTotal: number;
    categoryWinPct: number;
  }>;
  wonBreakdown: "source" | "segment";
  companyCurrency?: any;
  emptyMessage?: string;
}

export function WonByCategoryChart({
  data,
  wonBreakdown,
  companyCurrency,
  emptyMessage = "Data not available.",
}: WonByCategoryChartProps) {

  const entries = Object.entries(data)
    .filter(([, item]) => item.categoryWonTotal > 0)
    .toSorted((a, b) => {
      if (isCatchAllCategory(a[0]) !== isCatchAllCategory(b[0])) return isCatchAllCategory(a[0]) ? 1 : -1;
      return b[1].categoryWonTotal - a[1].categoryWonTotal;
    });

  if (!entries.length) {
    return (
      <div className="h-52">
        <p className="text-sm text-gray-500">
          {emptyMessage}
        </p>
      </div>
    )
  }

  return (
    <div className="h-52">
      {/* Headers */}
      <div className="flex flex-row justify-between items-center pt-3">
        <p className="text-xs text-gray-500 capitalize">
          {wonBreakdown}
        </p>
        <div className="flex flex-row gap-x-2 justify-end items-center">
          <p className="text-right text-xs text-gray-500 w-20">
            Won
          </p>
          <p className="text-right text-xs text-gray-500 w-16">
            Win rate
          </p>
        </div>
      </div>

      <Separator className="mt-2.5" />

      <div className="flex max-h-44 flex-col gap-y-2.5 overflow-y-auto pr-1 pt-2.5">
        {entries
          .slice(0, 6)
          .map(([category, won]) => (
            <div key={category} className="flex flex-row justify-between items-center">
              <div className="flex flex-row gap-x-0 flex-1 items-center">
                <p className="text-xs">
                  {category}
                </p>
              </div>
              <div className="flex flex-row gap-x-2 justify-end items-center">
                <p className="text-right text-xs">
                  {formatCurrencyValue(won.categoryWonTotal, companyCurrency, 1)}
                </p>
                <p className="text-right text-xs w-16">
                  {won.categoryWinPct.toFixed(0)}%
                </p>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

interface PipelineMovementChartProps {
  data: ManagementReviewPipelineMovementRow[];
  companyCurrency?: any;
}

interface ManagementReviewSankeyNodeProps {
  x?: number;
  y?: number;
  dx?: number;
  dy?: number;
  width?: number;
  height?: number;
  name?: string;
  payload?: {
    name?: string;
    fill?: string;
  };
}

function ManagementReviewSankeyNode(props: ManagementReviewSankeyNodeProps) {
  const x = props.x ?? 0;
  const y = props.y ?? 0;
  const width = props.dx ?? props.width ?? 10;
  const height = props.dy ?? props.height ?? 0;
  const name = props.name ?? props.payload?.name ?? "";
  const fill = props.payload?.fill ?? "#231942";

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={Math.max(height, 2)}
        fill={fill}
        rx={3}
      />
      <text
        x={x + width + 8}
        y={y + Math.max(height / 2, 8)}
        dominantBaseline="middle"
        className="fill-gray-700 text-[11px]"
      >
        {name}
      </text>
    </g>
  );
}

function getPipelineMovementValue(
  data: ManagementReviewPipelineMovementRow[],
  label: string,
) {
  return data.find(row => row.label === label)?.value ?? 0;
}

function buildPipelineMovementSankeyData(data: ManagementReviewPipelineMovementRow[]) {
  const startPipelineValue = Math.max(0, getPipelineMovementValue(data, "Pipeline at period start"));
  const newPipelineValue = Math.max(0, getPipelineMovementValue(data, "New pipeline"));
  const wonValue = Math.abs(getPipelineMovementValue(data, "Won"));
  const lostValue = Math.abs(getPipelineMovementValue(data, "Lost"));
  const stillOpenFromStartValue = Math.max(0, getPipelineMovementValue(data, "Still open from start pipeline"));
  const stillOpenFromNewValue = Math.max(0, getPipelineMovementValue(data, "Still open from new pipeline"));
  const stillOpenValue = stillOpenFromStartValue + stillOpenFromNewValue;

  const links = [
    {
      source: 0,
      target: 2,
      value: startPipelineValue,
      stroke: "#231942",
    },
    {
      source: 1,
      target: 2,
      value: newPipelineValue,
      stroke: "#231942",
    },
    {
      source: 2,
      target: 3,
      value: wonValue,
      stroke: "#231942",
    },
    {
      source: 2,
      target: 4,
      value: lostValue,
      stroke: "#231942",
    },
    {
      source: 2,
      target: 5,
      value: stillOpenValue,
      stroke: "#231942",
    },
  ].filter(link => link.value > 0);

  return {
    nodes: [
      { name: "Pipeline at start", fill: "#231942" },
      { name: "New pipeline", fill: "#231942" },
      { name: "Pipeline in period", fill: "#231942" },
      { name: "Won", fill: "#231942" },
      { name: "Lost", fill: "#231942" },
      { name: "Still open", fill: "#231942" },
    ],
    links,
    values: {
      startPipelineValue,
      newPipelineValue,
      wonValue,
      lostValue,
      stillOpenValue,
    },
  };
}

export function PipelineMovementChart({
  data,
  companyCurrency,
}: PipelineMovementChartProps) {
  const sankeyData = buildPipelineMovementSankeyData(data);
  const hasData = sankeyData.links.length > 0;

  if (!hasData) {
    return (
      <div className="h-[360px]">
        <p className="text-sm text-gray-500">
          Data not available.
        </p>
      </div>
    );
  }

  return (
    <div className="h-[360px]">
      <div className="grid grid-cols-5 gap-2 pb-4">
        <div className="rounded-[8px] border border-gray-200 bg-gray-50 px-3 py-2">
          <p className="text-[11px] font-medium uppercase text-gray-500">
            Pipeline at start
          </p>
          <p className="text-sm font-medium tabular-nums">
            {formatCurrencyValue(sankeyData.values.startPipelineValue, companyCurrency, 1)}
          </p>
        </div>
        <div className="rounded-[8px] border border-green-100 bg-green-50/70 px-3 py-2">
          <p className="text-[11px] font-medium uppercase text-green-700">
            New pipeline
          </p>
          <p className="text-sm font-medium tabular-nums">
            {formatCurrencyValue(sankeyData.values.newPipelineValue, companyCurrency, 1)}
          </p>
        </div>
        <div className="rounded-[8px] border border-green-100 bg-green-50/70 px-3 py-2">
          <p className="text-[11px] font-medium uppercase text-green-700">
            Won
          </p>
          <p className="text-sm font-medium tabular-nums">
            {formatCurrencyValue(sankeyData.values.wonValue, companyCurrency, 1)}
          </p>
        </div>
        <div className="rounded-[8px] border border-red-100 bg-red-50/70 px-3 py-2">
          <p className="text-[11px] font-medium uppercase text-red-700">
            Lost
          </p>
          <p className="text-sm font-medium tabular-nums">
            {formatCurrencyValue(sankeyData.values.lostValue, companyCurrency, 1)}
          </p>
        </div>
        <div className="rounded-[8px] border border-[#231942]/10 bg-[#231942]/5 px-3 py-2">
          <p className="text-[11px] font-medium uppercase text-[#231942]">
            Still open
          </p>
          <p className="text-sm font-medium tabular-nums">
            {formatCurrencyValue(sankeyData.values.stillOpenValue, companyCurrency, 1)}
          </p>
        </div>
      </div>
      <div className="h-[250px] rounded-[8px] border border-gray-100 bg-white">
        <ResponsiveContainer width="100%" height="100%">
          <Sankey
            data={sankeyData}
            dataKey="value"
            nameKey="name"
            nodePadding={36}
            nodeWidth={12}
            linkCurvature={0.5}
            iterations={32}
            sort={false}
            margin={{
              top: 18,
              right: 24,
              bottom: 18,
              left: 24,
            }}
            node={{
              fill: "#231942",
              stroke: "#FFFFFF",
              strokeWidth: 1,
            }}
            link={{
              stroke: "#231942",
              strokeOpacity: 0.22,
              fill: "none",
            }}
          >
            <RechartsTooltip
              formatter={(value: number) => formatCurrencyValue(value, companyCurrency, 1)}
            />
          </Sankey>
        </ResponsiveContainer>
      </div>
      <p className="pt-4 text-xs text-gray-500">
        Uses entered deal value to show pipeline movement during the selected period.
      </p>
    </div>
  );
}

interface HowWeWinAndLoseChartProps {
  data: ManagementReviewWinLossFlowRow[];
  companyCurrency?: any;
}

function buildWinLossSankeyData(data: ManagementReviewWinLossFlowRow[]) {
  const getTotalValue = (rows: ManagementReviewWinLossFlowRow[], key: "source" | "theme", value: string) =>
    rows
      .filter(row => row[key] === value)
      .reduce((sum, row) => sum + row.value, 0);
  const sourceNames = [...new Set(data.map(row => row.source))]
    .toSorted((a, b) => {
      if (isCatchAllCategory(a) !== isCatchAllCategory(b)) return isCatchAllCategory(a) ? 1 : -1;
      return getTotalValue(data, "source", b) - getTotalValue(data, "source", a);
    });
  const outcomeNames = [...new Set(data
    .filter(row => row.value > 0)
    .map(row => row.theme))]
    .toSorted((a, b) => {
      if (a === "Won" && b !== "Won") return 1;
      if (b === "Won" && a !== "Won") return -1;
      if (isCatchAllCategory(a) !== isCatchAllCategory(b)) return isCatchAllCategory(a) ? 1 : -1;
      return getTotalValue(data, "theme", b) - getTotalValue(data, "theme", a);
    });
  const nodes = [
    ...sourceNames.map(name => ({
      name,
      fill: "#231942",
    })),
    ...outcomeNames.map(name => ({
      name,
      fill: name === "Won" ? "#0D6E55" : "#9B3A46",
    })),
  ];
  const outcomeOffset = sourceNames.length;
  const links = data
    .map(row => ({
      source: sourceNames.indexOf(row.source),
      target: outcomeOffset + outcomeNames.indexOf(row.theme),
      value: row.value,
      stroke: "#231942",
    }))
    .filter(link => link.source >= 0 && link.target >= outcomeOffset && link.value > 0);

  return {
    nodes,
    links,
  };
}

export function HowWeWinAndLoseChart({
  data,
  companyCurrency,
}: HowWeWinAndLoseChartProps) {
  const sankeyData = buildWinLossSankeyData(data);
  const hasData = sankeyData.links.length > 0;

  if (!hasData) {
    return (
      <div className="h-[360px]">
        <p className="text-sm text-gray-500">
          Data not available.
        </p>
      </div>
    );
  }

  return (
    <div className="h-[360px]">
      <div className="h-[250px] rounded-[8px] border border-gray-100 bg-white">
        <ResponsiveContainer width="100%" height="100%">
          <Sankey
            data={sankeyData}
            dataKey="value"
            nameKey="name"
            nodePadding={24}
            nodeWidth={12}
            linkCurvature={0.46}
            iterations={32}
            sort={false}
            margin={{
              top: 18,
              right: 180,
              bottom: 18,
              left: 24,
            }}
            node={<ManagementReviewSankeyNode />}
            link={{
              stroke: "#231942",
              strokeOpacity: 0.18,
              fill: "none",
            }}
          >
            <RechartsTooltip
              formatter={(value: number) => formatCurrencyValue(value, companyCurrency, 1)}
            />
          </Sankey>
        </ResponsiveContainer>
      </div>
      <p className="pt-4 text-xs text-gray-500">
        Lost deal value is split across the strongest underlying risk indicators using the existing top-risk weighting.
      </p>
    </div>
  );
}

interface PipelineCoverageChartProps {
  data: TODO[],
}

export function PipelineCoverageChart({ data }: PipelineCoverageChartProps) {
  return (
    <div className="h-72">
      <ResponsiveContainer width="100%">

        <FunnelChart>
          <RechartsTooltip
            content={<CustomTooltipPipelineCoverageChart />}
          />

          <Funnel
            data={data}
            dataKey="width_for_chart"
            nameKey="stage"
            isAnimationActive
          >
            <LabelList
              dataKey="stage"
              position="center"
              fill="black"
              stroke="none"
              fontSize={12}
            />

            <LabelList
              dataKey="coverage_percentage_for_chart"
              position="center"
              fill="#fff"
              stroke="none"
              fontSize={10}
              formatter={(value: number) => value < 0 ? "" : `${value.toFixed(0)}%`}
            />
          </Funnel>
        </FunnelChart>
      </ResponsiveContainer>
    </div>
  );
}

function CustomTooltipPipelineCoverageChart({ active, payload, label }: RechartsTooltipProps<number, string>) {
  const { companyCurrency } = useAppStore((state) => state);
  const currencyCode = companyCurrency?.code ?? "SEK";

  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;

  if (row.coverage_percentage_for_chart < 0) return null;

  const close_probability = row?.close_probability ?? 0;

  const probabilityRangeEnd = close_probability * 100;
  const probabilityRangeStart = probabilityRangeEnd - 20;

  const coverage = row?.coverage_percentage ?? 0;
  const value = row?.value ?? 0;
  const proportional_target = row?.proportional_target ?? 0;

  return (
    <Card className="w-64 p-0" key={label}>
      <CardHeader className="p-4">
        <CardTitle className="text-xs font-bold font-display pb-2">
          Close probability {probabilityRangeStart.toFixed(0)} to {probabilityRangeEnd.toFixed(0)}%
        </CardTitle>

        <div className="space-y-1 text-xs">
          <div className="flex items-center justify-between gap-4">
            <span>Pipeline value</span>
            <span className="flex items-center gap-2">
              <span>{new Intl.NumberFormat("sv-SE").format(Math.round(value))} {currencyCode}</span>
            </span>
          </div>

          <div className="flex items-center justify-between gap-4 pb-1">
            <span>Required pipeline</span>
            <span className="flex items-center gap-2">
              <span>{new Intl.NumberFormat("sv-SE").format(Math.round(proportional_target))} {currencyCode}</span>
            </span>
          </div>

          <div className="border-t border-gray-200 pt-2 flex items-center justify-between gap-4">
            <span>Coverage</span>
            <span className="flex items-center gap-2">
              <span>{coverage < 1 ? coverage.toFixed(2) : coverage.toFixed(0)}%</span>
            </span>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
};


interface RiskBreakdownChartProps {
  data: TODO[],
  breakdown: DataBreakdown,
  selectedPipeline: string,
  setSelectedPipeline: (pipeline: string) => void,
  selectedOwner: string,
  setSelectedOwner: (owner: string) => void,
  companyCurrency: Database["public"]["Tables"]["currencies"]["Row"] | null
}

export function RiskBreakdownChart({
  data,
  breakdown,
  selectedPipeline,
  setSelectedPipeline,
  selectedOwner,
  setSelectedOwner,
  companyCurrency,
}: RiskBreakdownChartProps) {
  const standardHeight = 600;
  const singleBarHeight = 120;
  const pipelineBreakdownFullHeight = 200;
  const salesRepBreakdownFullHeight = 800;

  const isSingleBar =
    (selectedPipeline !== "all" && breakdown === "by_pipeline")
    || (selectedOwner !== "all" && breakdown === "by_sales_rep");

  const chartHeight = isSingleBar
    ? singleBarHeight
    : breakdown === "by_pipeline"
      ? pipelineBreakdownFullHeight
      : breakdown === "by_sales_rep"
        ? salesRepBreakdownFullHeight
        : standardHeight;

  const handleBarClick = (entry: any) => {
    if (breakdown === "by_pipeline") {
      if (selectedPipeline !== "all") {
        setSelectedPipeline("all");
        return;
      }

      const entryPipeline = entry?.payload?.pipeline_id;
      if (entryPipeline) {
        setSelectedPipeline(entryPipeline);
      }
    } else if (breakdown === "by_sales_rep") {
      if (selectedOwner !== "all") {
        setSelectedOwner("all");
        return;
      }

      const entryOwner = entry?.payload?.sales_rep_id;
      if (entryOwner) {
        setSelectedOwner(entryOwner);
      }
    }
  }

  return (
    <div style={{ height: chartHeight }}>
      <ResponsiveContainer width="100%">
        <BarChart
          data={data}
          layout="vertical"
          barSize={40}
        >
          <XAxis
            type="number"
            fontSize={12}
            tickFormatter={(value) => formatAxisTickValue(value, companyCurrency)}
          />
          <YAxis
            type="category"
            dataKey={breakdown === "by_month"
              ? "month"
              : breakdown === "by_sales_rep"
                ? "sales_rep"
                : "pipeline"}
            width={breakdown === "by_month"
              ? 80
              : 150}
            fontSize={12}
          />

          <RechartsTooltip
            cursor={{ fill: "#fff" }}
            content={<CustomTooltipRiskBreakdownChartWrapper breakdown={breakdown} />}
          />
          <Legend content={<CustomLegendRiskBreakdownChart />} />

          <Bar
            dataKey="without_risk"
            stackId="stack"
            fill="#3A3361"
            onClick={handleBarClick}
          />
          <Bar
            dataKey="at_risk"
            stackId="stack"
            fill="#9F86C0"
            radius={[0, 2, 2, 0]}
            onClick={handleBarClick}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function CustomLegendRiskBreakdownChart() {
  return (
    <div className="flex flex-row justify-center items-center gap-x-5 mt-1">
      <div className="flex flex-row justify-center items-center gap-x-1.5">
        <div className="w-3 h-3 rounded-full bg-[#3A3361]" />
        <p className="text-gray-500 text-sm">Expected</p>
      </div>
      <div className="flex flex-row justify-center items-center gap-x-1.5">
        <div className="w-3 h-3 rounded-full bg-[#9F86C0]" />
        <p className="text-gray-500 text-sm font-normal">At risk</p>
      </div>
    </div>
  );
};

interface CustomTooltipRiskBreakdownChartWrapperProps extends RechartsTooltipProps<number, string> {
  breakdown: DataBreakdown;
}

function CustomTooltipRiskBreakdownChartWrapper({ breakdown, ...props }: CustomTooltipRiskBreakdownChartWrapperProps) {
  function CustomTooltipRiskBreakdownChart({ active, payload, label }: RechartsTooltipProps<number, string>) {
    const { companyCurrency } = useAppStore((state) => state);
    const currencyCode = companyCurrency?.code ?? "SEK";

    if (!active || !payload || payload.length === 0) return null;
    const row = payload[0].payload;

    const pipeline = row?.pipeline ?? "No pipeline";
    const sales_rep = row?.sales_rep ?? "No owner";
    const month = row?.month ?? "";

    const monthAsDate = parse(month, "yyyy-MM", new Date());
    const readableMonth = format(monthAsDate, "MMMM yyyy");

    const title = breakdown === "by_pipeline"
      ? pipeline
      : breakdown === "by_sales_rep"
        ? sales_rep
        : breakdown === "by_month"
          ? readableMonth
          : "";

    const withoutRisk = row?.without_risk ?? 0;
    const atRisk = row?.at_risk ?? 0;

    const riskPercentage = (100 * atRisk) / (withoutRisk + atRisk);

    const formatter = new Intl.NumberFormat("sv-SE");

    return (
      <Card className="w-80 p-0" key={label}>
        <CardHeader className="p-4">
          <CardTitle className="text-xs font-bold font-display pb-2">
            {title}
          </CardTitle>

          <div className="space-y-1 text-xs">
            <div className="flex items-center justify-between gap-4">
              <span>Expected revenue</span>
              <span className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#3A3361]" />
                <span>{formatter.format(Math.round(withoutRisk))} {currencyCode}</span>
              </span>
            </div>

            <div className="flex items-center justify-between gap-4 pb-1">
              <span>At Risk</span>
              <span className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#9F86C0]" />
                <span>{formatter.format(Math.round(atRisk))} {currencyCode}</span>
              </span>
            </div>

            <div className="border-t border-gray-200 pt-2 flex items-center justify-between gap-4">
              <span>Risk %</span>
              <span>{riskPercentage > 10 ? riskPercentage.toFixed(0) : riskPercentage.toFixed(1)}%</span>
            </div>
          </div>
        </CardHeader>
      </Card>
    );
  };

  return <CustomTooltipRiskBreakdownChart {...props} />
}

type RiskHistory = {
  date: string;
  risk: number;
  weighted_risk: number;
  propertiesChanged: string[];
  riskIndicatorScores?: Partial<Record<RiskIndicator, number>>;
};

interface RiskHistoryChartProps {
  history: RiskHistory[];
}

type RiskHistoryWindow = "30" | "60" | "90" | "all";

type RiskHistoryActivity = {
  category: "Buyer activity" | "Sales activity" | "CRM update";
  label: string;
};

type RiskHistoryChartRow = RiskHistory & {
  activities: RiskHistoryActivity[];
  changedRiskIndicators: Array<{
    current: number;
    indicator: RiskIndicator;
    previous: number;
  }>;
  previousWeightedRisk: number | null;
  riskDelta: number | null;
  timestamp: number;
};

const riskHistoryWindows: Array<{ label: string; value: RiskHistoryWindow }> = [
  { label: "Past 30 days", value: "30" },
  { label: "Past 60 days", value: "60" },
  { label: "Past 90 days", value: "90" },
  { label: "Entire deal", value: "all" },
];

export function RiskHistoryChart({ history }: RiskHistoryChartProps) {
  const [window, setWindow] = useState<RiskHistoryWindow>(() => (
    history.length > 90 ? "90" : "all"
  ));

  const chartRows = useMemo(() => history.map((row, index): RiskHistoryChartRow => {
    const previous = history[index - 1];
    const indicatorKeys = new Set<RiskIndicator>([
      ...Object.keys(previous?.riskIndicatorScores ?? {}) as RiskIndicator[],
      ...Object.keys(row.riskIndicatorScores ?? {}) as RiskIndicator[],
    ]);
    const changedRiskIndicators = [...indicatorKeys]
      .map(indicator => ({
        current: row.riskIndicatorScores?.[indicator] ?? 0,
        indicator,
        previous: previous?.riskIndicatorScores?.[indicator] ?? 0,
      }))
      .filter(item => item.current !== item.previous)
      .toSorted((a, b) => (
        Math.abs(b.current - b.previous) - Math.abs(a.current - a.previous)
      ));
    const timestamp = new Date(`${row.date}T00:00:00`).getTime();

    return {
      ...row,
      activities: getRiskHistoryActivities(row.propertiesChanged),
      changedRiskIndicators,
      previousWeightedRisk: previous?.weighted_risk ?? null,
      riskDelta: previous ? row.weighted_risk - previous.weighted_risk : null,
      timestamp,
    };
  }), [history]);

  const visibleRows = useMemo(() => {
    if (!chartRows.length) return [];
    const lastTimestamp = chartRows[chartRows.length - 1]?.timestamp;
    if (lastTimestamp === undefined) return [];
    const cutoff = window === "all"
      ? Number.NEGATIVE_INFINITY
      : subDays(new Date(lastTimestamp), Number(window) - 1).getTime();
    const rowsInWindow = chartRows.filter(row => row.timestamp >= cutoff);

    return rowsInWindow.filter((row, index) => (
      index === 0
      || index === rowsInWindow.length - 1
      || row.riskDelta !== 0
      || row.activities.length > 0
    ));
  }, [chartRows, window]);

  if (!chartRows.length) {
    return (
      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium">Risk &amp; activity timeline</p>
          <p className="text-xs text-gray-500">Risk movement and recorded deal activity</p>
        </div>
        <div className="flex h-64 items-center justify-center rounded-md border border-dashed border-gray-200 text-sm text-gray-500">
          No risk history is available for this deal.
        </div>
      </div>
    );
  }

  const firstVisibleRow = visibleRows[0];
  const lastVisibleRow = visibleRows[visibleRows.length - 1];
  const windowChange = firstVisibleRow && lastVisibleRow
    ? lastVisibleRow.weighted_risk - firstVisibleRow.weighted_risk
    : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium">Risk &amp; activity timeline</p>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="Risk and activity timeline info"
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full text-gray-400 hover:bg-gray-50 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5E548E]"
                >
                  <InfoIcon className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs leading-5">
                Historical risk is reconstructed from available CRM history using the approved risk model. Activity and risk changes on the same date are related in time, but one does not necessarily cause the other.
              </TooltipContent>
            </Tooltip>
          </div>
          <p className="text-xs text-gray-500">
            Risk movement and recorded deal activity
          </p>
        </div>

        <Select value={window} onValueChange={(value) => setWindow(value as RiskHistoryWindow)}>
          <SelectTrigger aria-label="Risk history period" className="h-8 w-[142px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {riskHistoryWindows.map(option => (
              <SelectItem key={option.value} value={option.value} className="text-xs">
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2 border-y border-gray-100 py-2 text-xs text-gray-500 sm:flex-row sm:items-center sm:justify-between">
        <p>
          Current risk <span className="font-medium tabular-nums text-gray-900">{formatRiskPoints(lastVisibleRow?.weighted_risk)}</span>
          <span className="mx-1.5 text-gray-300">•</span>
          <span className={cn(
            "font-medium tabular-nums",
            windowChange > 0 ? "text-red-700" : windowChange < 0 ? "text-[#1D819A]" : "text-gray-600",
          )}>
            {windowChange > 0 ? "+" : ""}{formatRiskPoints(windowChange)}
          </span>
          {" "}in period
        </p>
        <div className="flex items-center gap-3" aria-label="Risk levels">
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-green-700/70" />Low 0–25</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500/80" />Medium 26–50</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-700/70" />High 51–100</span>
        </div>
      </div>

      {visibleRows.length < 2 ? (
        <div className="flex h-64 items-center justify-center rounded-md border border-dashed border-gray-200 px-6 text-center text-sm text-gray-500">
          Risk history starts today. More history will appear as the deal changes.
        </div>
      ) : (
        <div className="h-80">
          <ResponsiveContainer width="100%">
            <LineChart data={visibleRows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <ReferenceArea y1={0} y2={25} fill="#15803D" fillOpacity={0.035} strokeOpacity={0} />
              <ReferenceArea y1={25} y2={50} fill="#F59E0B" fillOpacity={0.045} strokeOpacity={0} />
              <ReferenceArea y1={50} y2={100} fill="#B91C1C" fillOpacity={0.035} strokeOpacity={0} />
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#E5E7EB" />

              <XAxis
                type="number"
                dataKey="timestamp"
                domain={["dataMin", "dataMax"]}
                tick={{ fontSize: 11, fill: "#6B7280" }}
                tickFormatter={(value) => format(new Date(value), "MMM d")}
                tickLine={false}
                axisLine={{ stroke: "#D1D5DB" }}
                tickMargin={8}
                minTickGap={48}
              />

              <YAxis
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
                tick={{ fontSize: 11, fill: "#6B7280" }}
                tickLine={false}
                axisLine={false}
                width={32}
              />

              <RechartsTooltip
                content={<CustomTooltipRiskHistoryChart />}
                cursor={{ stroke: "#9CA3AF", strokeDasharray: "3 3" }}
              />

              <Line
                type="stepAfter"
                dataKey="weighted_risk"
                stroke="#3A3361"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={({ cx, cy, payload }: {
                  cx?: number;
                  cy?: number;
                  payload?: RiskHistoryChartRow;
                }) => {
                  const hasMeaningfulActivity = payload?.activities.some(activity => (
                    activity.category !== "CRM update"
                  ));
                  if (cx === undefined || cy === undefined || !hasMeaningfulActivity) return <></>;

                  return (
                    <g>
                      <circle
                        aria-hidden="true"
                        cx={cx}
                        cy={cy}
                        r={24}
                        fill="transparent"
                        pointerEvents="all"
                      />
                      <circle
                        aria-hidden="true"
                        cx={cx}
                        cy={cy}
                        r={5}
                        fill="#3A3361"
                        pointerEvents="none"
                        stroke="white"
                        strokeWidth={2}
                      />
                    </g>
                  );
                }}
                activeDot={{ r: 6, fill: "#3A3361", stroke: "white", strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function CustomTooltipRiskHistoryChart({
  active,
  payload,
}: TooltipProps<number, string>) {
  if (!active || !payload || payload.length === 0) return null;

  const row = payload[0].payload as RiskHistoryChartRow;
  return (
    <Card className="w-72 p-0 shadow-md border-muted">
      <CardHeader className="p-3 pb-2">
        <CardTitle className="text-xs font-semibold">
          {format(new Date(row.timestamp), "MMM d, yyyy")}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-3 pt-2 space-y-2 text-xs">
        <div className="flex items-center justify-between">
          <p className="font-medium">Risk score</p>
          <p className={cn(
            "font-medium tabular-nums",
            (row.riskDelta ?? 0) > 0 ? "text-red-700" : (row.riskDelta ?? 0) < 0 ? "text-[#1D819A]" : "text-gray-700",
          )}>
            {row.previousWeightedRisk === null
              ? formatRiskPoints(row.weighted_risk)
              : formatRiskPointChange(row.previousWeightedRisk, row.weighted_risk)}
          </p>
        </div>

        {row.changedRiskIndicators.length > 0 && (
          <>
            <Separator />
            <div className="space-y-1.5">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Risk changed</p>
              {row.changedRiskIndicators.slice(0, 4).map(item => (
                <div key={item.indicator} className="flex items-center justify-between gap-3">
                  <span className="text-gray-600">{mapRiskName(item.indicator)}</span>
                  <span className="shrink-0 tabular-nums text-gray-500">
                    {formatRiskPointChange(item.previous, item.current)}
                  </span>
                </div>
              ))}
              {row.changedRiskIndicators.length > 4 && (
                <p className="text-[11px] text-gray-400">+{row.changedRiskIndicators.length - 4} more</p>
              )}
            </div>
          </>
        )}

        {row.activities.length > 0 && (
          <>
            <Separator />
            <div className="space-y-1.5">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Activity recorded</p>
              {row.activities.slice(0, 6).map(activity => (
                <div key={`${activity.category}-${activity.label}`} className="flex items-center justify-between gap-3">
                  <span className="text-gray-600">{activity.label}</span>
                  <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
                    {activity.category}
                  </span>
                </div>
              ))}
              {row.activities.length > 6 && (
                <p className="text-[11px] text-gray-400">+{row.activities.length - 6} more</p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function getRiskHistoryActivityCategory(property: string): RiskHistoryActivity["category"] {
  switch (property) {
    case "hs_latest_sales_email_reply_date":
      return "Buyer activity";
    case "hs_next_step":
    case "hs_number_of_scheduled_meetings":
    case "notes_last_updated":
    case "notes_next_activity_date":
      return "Sales activity";
    default:
      return "CRM update";
  }
}

function getRiskHistoryActivities(propertiesChanged: string[]): RiskHistoryActivity[] {
  return propertiesChanged
    .filter(property => property !== "today")
    .map(property => ({
      category: getRiskHistoryActivityCategory(property),
      label: mapHubspotPropertyNameToReadableCopy(property),
    }))
    .filter(activity => activity.label.length > 0)
    .filter((activity, index, activities) => (
      index === 0
      || activity.label !== activities[index - 1]?.label
      || activity.category !== activities[index - 1]?.category
    ));
}

function mapHubspotPropertyNameToReadableCopy(property: string) {
  switch (property) {
    case "amount":
    case "amount_in_home_currency":
      return "Deal amount updated";
    case "closedate":
      return "Close date pushed";
    case "createdate":
      return "Deal created";
    case "deal_currency_code":
      return "Deal currency updated";
    case "dealname":
      return "Deal name changed";
    case "dealstage":
      return "Moved to new stage";
    case "hs_is_closed":
      return "Deal closed";
    case "hs_is_closed_lost":
      return "Deal marked as lost";
    case "hs_is_closed_won":
      return "Deal marked as won";
    case "hs_is_stalled":
      return "Deal stalled";
    case "hs_lastmodifieddate":
      return "";
    case "hs_next_step":
      return "Next step updated";
    case "hs_number_of_scheduled_meetings":
      return "Scheduled meeting";
    case "hubspot_owner_id":
      return "Deal owner changed";
    case "pipeline":
      return "Changed pipeline";
    case "today":
      return "Today";
    case "hs_deal_stage_probability":
    case "hs_deal_stage_probability_shadow":
      return "";
    case "notes_last_updated":
    case "notes_next_activity_date":
      return "Notes updated";
    case "hs_latest_sales_email_reply_date":
      return "Replied to email";
    default:
      return `'${property}' updated`;
  }
}

// FIXME: Copied from dashboard/components/overview.tsx
function formatAxisTickValue(
  value: number,
  companyCurrency: Database["public"]["Tables"]["currencies"]["Row"] | null,
  decimalPlaces = 0,
) {
  const isNegative = value < 0;
  const absoluteValue = Math.abs(value);

  let formattedValue =
    absoluteValue >= 1_000_000
      ? `${(absoluteValue / 1_000_000).toFixed(decimalPlaces)}M`
      : absoluteValue >= 1_000
        ? `${(absoluteValue / 1_000).toFixed(decimalPlaces)}K`
        : absoluteValue.toFixed(decimalPlaces);

  if (isNegative) {
    formattedValue = `-${formattedValue}`
  }

  return `${formattedValue} ${companyCurrency?.code ?? "SEK"}`;
}
